/**
 * BL Academy authoring — super admin only.
 *
 * Three things are curated here: the lessons themselves (a YouTube link plus
 * the teaching material around it), the buyer toolkit worksheets, and the
 * Weekly Deal Room case study.
 *
 * Unlike the Resource library this console has NO platform-admin twin: the API
 * gates every write behind require_super_admin, so there is nothing to share
 * with a second client and this page talks to `superAdminApi` directly.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Archive,
  ExternalLink,
  Eye,
  EyeOff,
  FolderOpen,
  GraduationCap,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";

import PublishConfirmationModal from "@/components/PublishConfirmationModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";
import {
  ACADEMY_STATUS_LABELS,
  ACADEMY_STATUSES,
  CARD_TONE_LABELS,
  CARD_TONES,
  formatDuration,
  formatPublishedDate,
  parseDuration,
  youtubeThumbnail,
  type AcademyCaseStudy,
  type AcademyLesson,
  type AcademyRoleOption,
  type AcademyStage,
  type AcademyStatus,
  type AcademyToolkitItem,
  type PaginatedAcademyLessons,
} from "@/lib/academy";
import { cn } from "@/lib/utils";

import { superAdminApi, SuperAdminApiError } from "./session";

const PAGE_SIZE = 10;

const SELECT_CLASS =
  "w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

const STATUS_PILL: Record<AcademyStatus, string> = {
  published: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
  draft: "border-border/70 bg-muted/40 text-muted-foreground",
  archived: "border-amber-500/30 bg-amber-500/10 text-amber-600",
};

type Tab = "lessons" | "worksheets" | "case";

const TABS: { key: Tab; label: string }[] = [
  { key: "lessons", label: "Lessons" },
  { key: "worksheets", label: "Buyer worksheets" },
  { key: "case", label: "Weekly Deal Room" },
];

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof SuperAdminApiError || err instanceof Error ? err.message : fallback;
}

export default function SuperAdminAcademyPage() {
  const [tab, setTab] = useState<Tab>("lessons");
  const [stages, setStages] = useState<AcademyStage[]>([]);
  const [roles, setRoles] = useState<AcademyRoleOption[]>([]);

  // Both lists are needed by every tab (the stage picker, the role picker, and
  // the lesson dropdowns the other two tabs use), so they load once here.
  useEffect(() => {
    superAdminApi.get<AcademyStage[]>("/academy/admin/stages").then(setStages).catch(() => {});
    superAdminApi.get<AcademyRoleOption[]>("/academy/admin/roles").then(setRoles).catch(() => {});
  }, []);

  return (
    <PageShell>
      <PageHero
        eyebrow="Content"
        icon={GraduationCap}
        title="BL"
        titleAccent="Academy"
        description="Curate the YouTube lessons buyers and BL advisors learn from, the worksheets that turn a lesson into a next step, and the weekly deal room case."
      />

      <div className="flex flex-wrap gap-1.5 border-b border-border pb-px">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              "-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors",
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "lessons" && <LessonsTab stages={stages} roles={roles} />}
      {tab === "worksheets" && <WorksheetsTab />}
      {tab === "case" && <CaseTab />}
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Lessons
// ─────────────────────────────────────────────────────────────────────
interface LessonForm {
  title: string;
  summary: string;
  category: string;
  stage: string;
  youtube_url: string;
  thumbnail_url: string;
  /** Free text as the author types it ("2:30"), parsed on submit. */
  duration: string;
  card_headline: string;
  card_tone: string;
  storyboard: { label: string; headline: string; body: string }[];
  key_takeaways: string[];
  transcript: string;
  action_prompt: string;
  quiz_question: string;
  quiz_answers: string[];
  quiz_correct_index: number | null;
  quiz_explanation: string;
  display_order: string;
  status: AcademyStatus;
  role_ids: number[];
}

const EMPTY_LESSON: LessonForm = {
  title: "",
  summary: "",
  category: "",
  stage: "",
  youtube_url: "",
  thumbnail_url: "",
  duration: "",
  card_headline: "",
  card_tone: "",
  storyboard: [{ label: "", headline: "", body: "" }],
  key_takeaways: [""],
  transcript: "",
  action_prompt: "",
  quiz_question: "",
  quiz_answers: ["", ""],
  quiz_correct_index: null,
  quiz_explanation: "",
  display_order: "0",
  status: "draft",
  role_ids: [],
};

function formFromLesson(l: AcademyLesson): LessonForm {
  return {
    title: l.title,
    summary: l.summary ?? "",
    category: l.category ?? "",
    stage: l.stage,
    youtube_url: l.youtube_url ?? "",
    thumbnail_url: l.thumbnail_url ?? "",
    duration: l.duration_seconds > 0 ? formatDuration(l.duration_seconds) : "",
    card_headline: l.card_headline ?? "",
    card_tone: l.card_tone ?? "",
    // Always leave one blank frame to type into.
    storyboard: l.storyboard.length
      ? [...l.storyboard.map((sc) => ({ ...sc }))]
      : [{ label: "", headline: "", body: "" }],
    // Always leave one empty row to type into.
    key_takeaways: l.key_takeaways.length ? [...l.key_takeaways, ""] : [""],
    transcript: l.transcript ?? "",
    action_prompt: l.action_prompt ?? "",
    quiz_question: l.quiz_question ?? "",
    quiz_answers: l.quiz_answers.length ? [...l.quiz_answers] : ["", ""],
    quiz_correct_index: l.quiz_correct_index,
    quiz_explanation: l.quiz_explanation ?? "",
    display_order: String(l.display_order),
    status: l.status,
    role_ids: [...l.role_ids],
  };
}

function LessonsTab({ stages, roles }: { stages: AcademyStage[]; roles: AcademyRoleOption[] }) {
  const toast = useToast();
  const [rows, setRows] = useState<AcademyLesson[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");

  const [editing, setEditing] = useState<AcademyLesson | null>(null);
  const [form, setForm] = useState<LessonForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishTarget, setPublishTarget] = useState<AcademyLesson | null>(null);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (search.trim()) params.set("search", search.trim());
    if (statusFilter) params.set("status", statusFilter);
    if (stageFilter) params.set("stage", stageFilter);
    superAdminApi
      .get<PaginatedAcademyLessons>(`/academy/admin/lessons?${params.toString()}`)
      .then((data) => {
        setRows(data.items);
        setTotal(data.total);
      })
      .catch((err) => {
        setRows([]);
        toast.error(errorMessage(err, "Couldn't load lessons."));
      });
    // `toast` is stable from context; leaving it out keeps this from refiring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, stageFilter]);

  useEffect(() => {
    const t = window.setTimeout(load, search ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [load, search]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_LESSON, stage: stages[0]?.key ?? "" });
  };

  const openEdit = (lesson: AcademyLesson) => {
    setEditing(lesson);
    setForm(formFromLesson(lesson));
  };

  const submit = async () => {
    if (!form) return;
    const seconds = parseDuration(form.duration);
    if (seconds === null) {
      toast.error("Enter the runtime as minutes and seconds, e.g. 2:30.");
      return;
    }
    const orderValue = Number(form.display_order);
    if (!Number.isFinite(orderValue) || orderValue < 0) {
      toast.error("Display order must be zero or more.");
      return;
    }

    const body = {
      title: form.title,
      summary: form.summary,
      category: form.category,
      stage: form.stage,
      youtube_url: form.youtube_url,
      thumbnail_url: form.thumbnail_url || null,
      duration_seconds: seconds,
      card_headline: form.card_headline,
      card_tone: form.card_tone || null,
      storyboard: form.storyboard,
      key_takeaways: form.key_takeaways,
      transcript: form.transcript,
      action_prompt: form.action_prompt,
      quiz_question: form.quiz_question,
      quiz_answers: form.quiz_answers,
      quiz_correct_index: form.quiz_correct_index,
      quiz_explanation: form.quiz_explanation,
      display_order: orderValue,
      status: form.status,
      role_ids: form.role_ids,
    };

    setSaving(true);
    try {
      if (editing) {
        await superAdminApi.put(`/academy/admin/lessons/${editing.id}`, body);
        toast.success("Lesson updated.");
      } else {
        await superAdminApi.post("/academy/admin/lessons", body);
        toast.success("Lesson created.");
      }
      setForm(null);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save that lesson."));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (lesson: AcademyLesson, status: AcademyStatus) => {
    // Publishing makes a lesson visible to real users, so it goes through the
    // same confirmation the Resource console uses.
    if (status === "published") {
      setPublishTarget(lesson);
      return;
    }
    try {
      await superAdminApi.patch(`/academy/admin/lessons/${lesson.id}/status`, { status });
      toast.success(`Lesson ${status === "archived" ? "archived" : "unpublished"}.`);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't change that lesson's status."));
    }
  };

  const confirmPublish = async () => {
    if (!publishTarget) return;
    setPublishing(true);
    try {
      await superAdminApi.patch(`/academy/admin/lessons/${publishTarget.id}/status`, {
        status: "published",
      });
      toast.success("Lesson published.");
      setPublishTarget(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't publish that lesson."));
    } finally {
      setPublishing(false);
    }
  };

  const remove = async (lesson: AcademyLesson) => {
    if (!window.confirm(`Delete "${lesson.title}"? This also removes everyone's progress on it.`)) {
      return;
    }
    try {
      await superAdminApi.delete(`/academy/admin/lessons/${lesson.id}`);
      toast.success("Lesson deleted.");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't delete that lesson."));
    }
  };

  const roleNames = (ids: number[]) =>
    ids
      .map((id) => roles.find((r) => r.id === id)?.label)
      .filter(Boolean)
      .join(", ") || "—";

  return (
    <>
      <PagePanel>
        <PanelHeading
          title="Lessons"
          description="Each lesson is one YouTube video plus the teaching material around it."
          actions={
            <Button onClick={openNew} className="gap-2">
              <Plus className="h-4 w-4" />
              New lesson
            </Button>
          }
        />

        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search lessons"
              className="pl-9"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className={cn(SELECT_CLASS, "sm:w-44")}
          >
            <option value="">All statuses</option>
            {ACADEMY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ACADEMY_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={stageFilter}
            onChange={(e) => {
              setStageFilter(e.target.value);
              setPage(1);
            }}
            className={cn(SELECT_CLASS, "sm:w-56")}
          >
            <option value="">All stages</option>
            {stages.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {rows === null ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <EmptyPanel
            icon={GraduationCap}
            title="No lessons yet"
            description="Add a YouTube link and it becomes a lesson buyers and advisors can work through."
          />
        ) : (
          <div className="space-y-3">
            {rows.map((lesson) => (
              <div
                key={lesson.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:flex-row sm:items-center"
              >
                {lesson.thumbnail_url || lesson.youtube_video_id ? (
                  <img
                    src={lesson.thumbnail_url || youtubeThumbnail(lesson.youtube_video_id!)}
                    alt=""
                    loading="lazy"
                    className="h-20 w-full shrink-0 rounded-lg object-cover sm:w-32"
                  />
                ) : (
                  // Written, but no video attached yet. Say so plainly — this is
                  // the state every seeded lesson starts in.
                  <div className="flex h-20 w-full shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 px-2 text-center text-[0.62rem] font-semibold text-muted-foreground sm:w-32">
                    {lesson.storyboard.length > 0 ? "Storyboard" : "No video yet"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide",
                        STATUS_PILL[lesson.status],
                      )}
                    >
                      {ACADEMY_STATUS_LABELS[lesson.status]}
                    </span>
                    {lesson.category && (
                      <span className="text-[0.62rem] font-bold uppercase tracking-wide text-primary">
                        {lesson.category}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 truncate text-sm font-bold text-foreground">{lesson.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.68rem] text-muted-foreground">
                    <span>{lesson.stage_label}</span>
                    <span>{formatDuration(lesson.duration_seconds)}</span>
                    <span>Order {lesson.display_order}</span>
                    <span>Roles: {roleNames(lesson.role_ids)}</span>
                    <span>{formatPublishedDate(lesson.published_at)}</span>
                    {lesson.quiz_question && <span>Quiz ✓</span>}
                    {!lesson.youtube_video_id &&
                      (lesson.storyboard.length > 0 ? (
                        <span className="font-semibold text-muted-foreground">
                          Storyboard ({lesson.storyboard.length})
                        </span>
                      ) : (
                        <span className="font-semibold text-amber-600">Needs a video</span>
                      ))}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                  {lesson.youtube_url && (
                    <IconButton
                      title="Open on YouTube"
                      as="a"
                      href={lesson.youtube_url}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </IconButton>
                  )}
                  <IconButton title="Edit" onClick={() => openEdit(lesson)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </IconButton>
                  {lesson.status === "published" ? (
                    <IconButton title="Unpublish" onClick={() => setStatus(lesson, "draft")}>
                      <EyeOff className="h-3.5 w-3.5" />
                    </IconButton>
                  ) : (
                    <IconButton title="Publish" onClick={() => setStatus(lesson, "published")}>
                      <Eye className="h-3.5 w-3.5" />
                    </IconButton>
                  )}
                  {lesson.status !== "archived" && (
                    <IconButton title="Archive" onClick={() => setStatus(lesson, "archived")}>
                      <Archive className="h-3.5 w-3.5" />
                    </IconButton>
                  )}
                  <IconButton title="Delete" danger onClick={() => remove(lesson)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5">
          <Pagination page={page} totalElements={total} pageSize={PAGE_SIZE} onChange={setPage} />
        </div>
      </PagePanel>

      {form && (
        <LessonFormModal
          form={form}
          setForm={setForm}
          stages={stages}
          roles={roles}
          isEdit={!!editing}
          saving={saving}
          onCancel={() => {
            setForm(null);
            setEditing(null);
          }}
          onSubmit={submit}
        />
      )}

      <PublishConfirmationModal
        isOpen={!!publishTarget}
        title="Publish lesson"
        message="Once published, this lesson appears in BL Academy for every role it targets."
        resourceName={publishTarget?.title}
        resourceType="BL Academy lesson"
        currentStatus={publishTarget ? ACADEMY_STATUS_LABELS[publishTarget.status] : null}
        targetAudience={publishTarget ? roleNames(publishTarget.role_ids) : null}
        isLoading={publishing}
        onConfirm={confirmPublish}
        onCancel={() => setPublishTarget(null)}
      />
    </>
  );
}

function LessonFormModal({
  form,
  setForm,
  stages,
  roles,
  isEdit,
  saving,
  onCancel,
  onSubmit,
}: {
  form: LessonForm;
  setForm: (f: LessonForm) => void;
  stages: AcademyStage[];
  roles: AcademyRoleOption[];
  isEdit: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const set = <K extends keyof LessonForm>(key: K, value: LessonForm[K]) =>
    setForm({ ...form, [key]: value });

  /** Keeps exactly one trailing blank row so there is always somewhere to type. */
  const setTakeaway = (index: number, value: string) => {
    const next = [...form.key_takeaways];
    next[index] = value;
    const trimmed = next.filter((v, i) => v.trim() !== "" || i === next.length - 1);
    if (trimmed[trimmed.length - 1]?.trim() !== "") trimmed.push("");
    set("key_takeaways", trimmed);
  };

  const setAnswer = (index: number, value: string) => {
    const next = [...form.quiz_answers];
    next[index] = value;
    set("quiz_answers", next);
  };

  const toggleRole = (id: number) =>
    set(
      "role_ids",
      form.role_ids.includes(id) ? form.role_ids.filter((r) => r !== id) : [...form.role_ids, id],
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Edit lesson" : "New lesson"}
    >
      <div className="my-6 w-full max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <strong className="text-sm font-bold text-foreground">
            {isEdit ? "Edit lesson" : "New lesson"}
          </strong>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="space-y-5 p-5"
        >
          <Field label="YouTube link">
            <Input
              value={form.youtube_url}
              onChange={(e) => set("youtube_url", e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
            />
            <p className="mt-1 text-[0.68rem] text-muted-foreground">
              A watch, youtu.be, Shorts or live link all work — the video id is read out of it.
              Leave blank to write the lesson now and attach the video later. Use{" "}
              <strong>Unlisted</strong>, not Private — YouTube blocks private videos from
              embedding. You can't publish a lesson until it has a link.
            </p>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" required>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} required />
            </Field>
            <Field label="Category">
              <Input
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                placeholder="Financial foundations"
              />
            </Field>
          </div>

          <Field label="One-line summary">
            <Textarea
              rows={2}
              value={form.summary}
              onChange={(e) => set("summary", e.target.value)}
              placeholder="Follow the numbers. Understand what you could actually take home."
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Learning path stage" required>
              <select
                value={form.stage}
                onChange={(e) => set("stage", e.target.value)}
                className={SELECT_CLASS}
                required
              >
                <option value="">Select a stage</option>
                {stages.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Runtime">
              <Input
                value={form.duration}
                onChange={(e) => set("duration", e.target.value)}
                placeholder="2:30"
              />
            </Field>
            <Field label="Display order">
              <Input
                type="number"
                min={0}
                value={form.display_order}
                onChange={(e) => set("display_order", e.target.value)}
              />
            </Field>
          </div>

          {/* ── Card design ── */}
          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Lesson card
            </legend>
            <Field label="Card headline">
              <Textarea
                rows={2}
                value={form.card_headline}
                onChange={(e) => set("card_headline", e.target.value)}
                placeholder="Real expense. Or a real add-back?"
              />
              <p className="mt-1 text-[0.68rem] text-muted-foreground">
                The short statement set large on the tile — the hook, not the title. Keep it
                under about 8 words. Leave blank to print the title instead.
              </p>
            </Field>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Tile colour">
                <select
                  value={form.card_tone}
                  onChange={(e) => set("card_tone", e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">Auto (alternates down the grid)</option>
                  {CARD_TONES.map((t) => (
                    <option key={t} value={t}>
                      {CARD_TONE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Thumbnail URL">
                <Input
                  value={form.thumbnail_url}
                  onChange={(e) => set("thumbnail_url", e.target.value)}
                  placeholder="Used by the Continue hero"
                />
              </Field>
            </div>
          </fieldset>

          {/* ── Storyboard ── */}
          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Storyboard
            </legend>
            <p className="mb-3 text-[0.68rem] leading-6 text-muted-foreground">
              Played in place of the video until one is attached — a caption, a large statement,
              and a line of body copy per scene. Once a YouTube link is added the video takes
              over and this stays as the backup.
            </p>
            <div className="space-y-3">
              {form.storyboard.map((sc, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-border/70 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[0.62rem] font-bold uppercase tracking-wide text-muted-foreground">
                      Scene {i + 1}
                    </span>
                    {form.storyboard.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          set("storyboard", form.storyboard.filter((_, idx) => idx !== i))
                        }
                        className="text-[0.68rem] font-semibold text-rose-600 transition-opacity hover:opacity-80"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <Input
                    value={sc.label}
                    onChange={(e) =>
                      set(
                        "storyboard",
                        form.storyboard.map((x, idx) =>
                          idx === i ? { ...x, label: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="Caption, e.g. THE QUESTION"
                  />
                  <Input
                    value={sc.headline}
                    onChange={(e) =>
                      set(
                        "storyboard",
                        form.storyboard.map((x, idx) =>
                          idx === i ? { ...x, headline: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="The statement set large"
                  />
                  <Textarea
                    rows={2}
                    value={sc.body}
                    onChange={(e) =>
                      set(
                        "storyboard",
                        form.storyboard.map((x, idx) =>
                          idx === i ? { ...x, body: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder="A line of supporting copy"
                  />
                </div>
              ))}
            </div>
            {form.storyboard.length < 8 && (
              <button
                type="button"
                onClick={() =>
                  set("storyboard", [...form.storyboard, { label: "", headline: "", body: "" }])
                }
                className="mt-2 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
              >
                + Add a scene
              </button>
            )}
          </fieldset>

          {/* ── Lesson detail ── */}
          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Lesson detail
            </legend>
            <Field label="Key takeaways">
              <div className="space-y-2">
                {form.key_takeaways.map((value, i) => (
                  <Input
                    key={i}
                    value={value}
                    onChange={(e) => setTakeaway(i, e.target.value)}
                    placeholder={i === 0 ? "One thing the viewer should walk away with" : "Add another"}
                  />
                ))}
              </div>
            </Field>
            <div className="mt-4">
              <Field label="Transcript">
                <Textarea
                  rows={4}
                  value={form.transcript}
                  onChange={(e) => set("transcript", e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Next-step prompt">
                <Input
                  value={form.action_prompt}
                  onChange={(e) => set("action_prompt", e.target.value)}
                  placeholder="Your next seller question"
                />
                <p className="mt-1 text-[0.68rem] text-muted-foreground">
                  The label above the note box where the viewer writes their next action.
                </p>
              </Field>
            </div>
          </fieldset>

          {/* ── Quiz ── */}
          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Quiz (optional)
            </legend>
            <Field label="Question">
              <Input
                value={form.quiz_question}
                onChange={(e) => set("quiz_question", e.target.value)}
                placeholder="Leave blank for no quiz"
              />
            </Field>
            {form.quiz_question.trim() && (
              <>
                <div className="mt-4 space-y-2">
                  <Label>Answers — select the correct one</Label>
                  {form.quiz_answers.map((value, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="academy-correct-answer"
                        checked={form.quiz_correct_index === i}
                        onChange={() => set("quiz_correct_index", i)}
                        aria-label={`Answer ${String.fromCharCode(65 + i)} is correct`}
                        className="h-4 w-4 shrink-0 accent-[var(--color-primary)]"
                      />
                      <Input
                        value={value}
                        onChange={(e) => setAnswer(i, e.target.value)}
                        placeholder={`Answer ${String.fromCharCode(65 + i)}`}
                      />
                      {form.quiz_answers.length > 2 && (
                        <button
                          type="button"
                          aria-label={`Remove answer ${String.fromCharCode(65 + i)}`}
                          onClick={() => {
                            const next = form.quiz_answers.filter((_, idx) => idx !== i);
                            setForm({
                              ...form,
                              quiz_answers: next,
                              // The marked answer moves with the list, and is
                              // cleared outright if it was the one removed.
                              quiz_correct_index:
                                form.quiz_correct_index === i
                                  ? null
                                  : form.quiz_correct_index !== null && form.quiz_correct_index > i
                                    ? form.quiz_correct_index - 1
                                    : form.quiz_correct_index,
                            });
                          }}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-rose-500/40 hover:text-rose-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {form.quiz_answers.length < 6 && (
                    <button
                      type="button"
                      onClick={() => set("quiz_answers", [...form.quiz_answers, ""])}
                      className="text-xs font-semibold text-primary transition-opacity hover:opacity-80"
                    >
                      + Add an answer
                    </button>
                  )}
                </div>
                <div className="mt-4">
                  <Field label="Explanation">
                    <Textarea
                      rows={2}
                      value={form.quiz_explanation}
                      onChange={(e) => set("quiz_explanation", e.target.value)}
                      placeholder="Shown once the viewer answers."
                    />
                  </Field>
                </div>
              </>
            )}
          </fieldset>

          {/* ── Publishing ── */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value as AcademyStatus)}
                className={SELECT_CLASS}
              >
                {ACADEMY_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {ACADEMY_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Publish to">
              <div className="flex flex-wrap gap-3 pt-1.5">
                {roles.map((role) => (
                  <label key={role.id} className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.role_ids.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                      className="h-4 w-4 accent-[var(--color-primary)]"
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            </Field>
          </div>

          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create lesson"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Buyer worksheets
// ─────────────────────────────────────────────────────────────────────
function WorksheetsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<AcademyToolkitItem[] | null>(null);
  const [lessons, setLessons] = useState<AcademyLesson[]>([]);
  const [editing, setEditing] = useState<AcademyToolkitItem | null>(null);
  const [draft, setDraft] = useState<{
    title: string;
    description: string;
    lesson_id: string;
    display_order: string;
    is_active: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    superAdminApi
      .get<AcademyToolkitItem[]>("/academy/admin/toolkit")
      .then(setRows)
      .catch((err) => {
        setRows([]);
        toast.error(errorMessage(err, "Couldn't load worksheets."));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // The lesson dropdown: one page large enough to cover the whole library.
    superAdminApi
      .get<PaginatedAcademyLessons>("/academy/admin/lessons?page=1&page_size=100")
      .then((d) => setLessons(d.items))
      .catch(() => {});
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setDraft({ title: "", description: "", lesson_id: "", display_order: "0", is_active: true });
  };

  const openEdit = (item: AcademyToolkitItem) => {
    setEditing(item);
    setDraft({
      title: item.title,
      description: item.description ?? "",
      lesson_id: item.lesson_id ?? "",
      display_order: String(item.display_order),
      is_active: item.is_active,
    });
  };

  const submit = async () => {
    if (!draft) return;
    const body = {
      title: draft.title,
      description: draft.description,
      lesson_id: draft.lesson_id || null,
      display_order: Number(draft.display_order) || 0,
      is_active: draft.is_active,
    };
    setSaving(true);
    try {
      if (editing) {
        await superAdminApi.put(`/academy/admin/toolkit/${editing.id}`, body);
        toast.success("Worksheet updated.");
      } else {
        await superAdminApi.post("/academy/admin/toolkit", body);
        toast.success("Worksheet created.");
      }
      setDraft(null);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save that worksheet."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: AcademyToolkitItem) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await superAdminApi.delete(`/academy/admin/toolkit/${item.id}`);
      toast.success("Worksheet deleted.");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't delete that worksheet."));
    }
  };

  return (
    <>
      <PagePanel>
        <PanelHeading
          title="Buyer worksheets"
          description="The cards on the reader's toolkit tab. Each one points at a lesson."
          actions={
            <Button onClick={openNew} className="gap-2">
              <Plus className="h-4 w-4" />
              New worksheet
            </Button>
          }
        />

        {rows === null ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <EmptyPanel
            icon={FolderOpen}
            title="No worksheets yet"
            description="Worksheets turn a lesson into a prompt the buyer can act on."
          />
        ) : (
          <div className="space-y-3">
            {rows.map((item) => (
              <div
                key={item.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-sm font-bold text-foreground">{item.title}</h3>
                    {!item.is_active && (
                      <span className="rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">
                        Hidden
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="mt-1 text-xs leading-6 text-muted-foreground">{item.description}</p>
                  )}
                  <div className="mt-1 text-[0.68rem] text-muted-foreground">
                    Order {item.display_order} · Opens: {item.lesson_title ?? "— no lesson linked"}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <IconButton title="Edit" onClick={() => openEdit(item)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton title="Delete" danger onClick={() => remove(item)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </PagePanel>

      {draft && (
        <SmallFormModal
          title={editing ? "Edit worksheet" : "New worksheet"}
          saving={saving}
          onCancel={() => {
            setDraft(null);
            setEditing(null);
          }}
          onSubmit={submit}
        >
          <Field label="Title" required>
            <Input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              required
            />
          </Field>
          <Field label="Description">
            <Textarea
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </Field>
          <Field label="Opens this lesson">
            <select
              value={draft.lesson_id}
              onChange={(e) => setDraft({ ...draft, lesson_id: e.target.value })}
              className={SELECT_CLASS}
            >
              <option value="">No lesson</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Display order">
              <Input
                type="number"
                min={0}
                value={draft.display_order}
                onChange={(e) => setDraft({ ...draft, display_order: e.target.value })}
              />
            </Field>
            <Field label="Visible">
              <label className="inline-flex items-center gap-2 pt-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                  className="h-4 w-4 accent-[var(--color-primary)]"
                />
                Show on the toolkit tab
              </label>
            </Field>
          </div>
        </SmallFormModal>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Weekly Deal Room
// ─────────────────────────────────────────────────────────────────────
interface CaseForm {
  eyebrow: string;
  badge: string;
  headline: string;
  teaser: string;
  prompt: string;
  metrics: { label: string; value: string }[];
  options: { label: string; feedback: string }[];
  lesson_id: string;
  display_order: string;
  status: AcademyStatus;
}

const EMPTY_CASE: CaseForm = {
  eyebrow: "The Weekly Deal Room",
  badge: "Fictional case study",
  headline: "",
  teaser: "",
  prompt: "",
  metrics: [{ label: "", value: "" }],
  options: [
    { label: "", feedback: "" },
    { label: "", feedback: "" },
  ],
  lesson_id: "",
  display_order: "0",
  status: "draft",
};

function CaseTab() {
  const toast = useToast();
  const [rows, setRows] = useState<AcademyCaseStudy[] | null>(null);
  const [lessons, setLessons] = useState<AcademyLesson[]>([]);
  const [editing, setEditing] = useState<AcademyCaseStudy | null>(null);
  const [form, setForm] = useState<CaseForm | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    superAdminApi
      .get<AcademyCaseStudy[]>("/academy/admin/case-studies")
      .then(setRows)
      .catch((err) => {
        setRows([]);
        toast.error(errorMessage(err, "Couldn't load case studies."));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    superAdminApi
      .get<PaginatedAcademyLessons>("/academy/admin/lessons?page=1&page_size=100")
      .then((d) => setLessons(d.items))
      .catch(() => {});
  }, [load]);

  const openEdit = (c: AcademyCaseStudy) => {
    setEditing(c);
    setForm({
      eyebrow: c.eyebrow,
      badge: c.badge ?? "",
      headline: c.headline,
      teaser: c.teaser ?? "",
      prompt: c.prompt ?? "",
      metrics: c.metrics.length ? c.metrics.map((m) => ({ ...m })) : [{ label: "", value: "" }],
      options: c.options.length
        ? c.options.map((o) => ({ ...o }))
        : [
            { label: "", feedback: "" },
            { label: "", feedback: "" },
          ],
      lesson_id: c.lesson_id ?? "",
      display_order: String(c.display_order),
      status: c.status,
    });
  };

  const submit = async () => {
    if (!form) return;
    const body = {
      eyebrow: form.eyebrow,
      badge: form.badge || null,
      headline: form.headline,
      teaser: form.teaser,
      prompt: form.prompt,
      metrics: form.metrics,
      options: form.options,
      lesson_id: form.lesson_id || null,
      display_order: Number(form.display_order) || 0,
      status: form.status,
    };
    setSaving(true);
    try {
      if (editing) {
        await superAdminApi.put(`/academy/admin/case-studies/${editing.id}`, body);
        toast.success("Case study updated.");
      } else {
        await superAdminApi.post("/academy/admin/case-studies", body);
        toast.success("Case study created.");
      }
      setForm(null);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save that case study."));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (c: AcademyCaseStudy, status: AcademyStatus) => {
    try {
      await superAdminApi.patch(`/academy/admin/case-studies/${c.id}/status`, { status });
      toast.success("Case study updated.");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't change that case study's status."));
    }
  };

  const remove = async (c: AcademyCaseStudy) => {
    if (!window.confirm(`Delete "${c.headline}"?`)) return;
    try {
      await superAdminApi.delete(`/academy/admin/case-studies/${c.id}`);
      toast.success("Case study deleted.");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't delete that case study."));
    }
  };

  const setRow = <T,>(list: T[], index: number, value: T): T[] =>
    list.map((item, i) => (i === index ? value : item));

  return (
    <>
      <PagePanel>
        <PanelHeading
          title="Weekly Deal Room"
          description="The scenario on the reader's right rail. The first published case in display order is the one shown."
          actions={
            <Button
              onClick={() => {
                setEditing(null);
                setForm({ ...EMPTY_CASE });
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New case study
            </Button>
          }
        />

        {rows === null ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <EmptyPanel
            icon={GraduationCap}
            title="No case studies yet"
            description="A case study asks the reader to make a call on a fictional deal, then explains each choice."
          />
        ) : (
          <div className="space-y-3">
            {rows.map((c, index) => (
              <div
                key={c.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide",
                        STATUS_PILL[c.status],
                      )}
                    >
                      {ACADEMY_STATUS_LABELS[c.status]}
                    </span>
                    {/* Only one case reaches the rail — say which, so a second
                        published case doesn't look broken. */}
                    {c.status === "published" &&
                      index === rows.findIndex((r) => r.status === "published" && r.is_active) && (
                        <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-primary">
                          Live on the rail
                        </span>
                      )}
                  </div>
                  <h3 className="mt-1 truncate text-sm font-bold text-foreground">{c.headline}</h3>
                  <div className="mt-1 text-[0.68rem] text-muted-foreground">
                    Order {c.display_order} · {c.options.length} options · {c.metrics.length} metrics
                    {c.lesson_title ? ` · Links to ${c.lesson_title}` : ""}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <IconButton title="Edit" onClick={() => openEdit(c)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </IconButton>
                  {c.status === "published" ? (
                    <IconButton title="Unpublish" onClick={() => setStatus(c, "draft")}>
                      <EyeOff className="h-3.5 w-3.5" />
                    </IconButton>
                  ) : (
                    <IconButton title="Publish" onClick={() => setStatus(c, "published")}>
                      <Eye className="h-3.5 w-3.5" />
                    </IconButton>
                  )}
                  <IconButton title="Delete" danger onClick={() => remove(c)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </PagePanel>

      {form && (
        <SmallFormModal
          title={editing ? "Edit case study" : "New case study"}
          wide
          saving={saving}
          onCancel={() => {
            setForm(null);
            setEditing(null);
          }}
          onSubmit={submit}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Eyebrow">
              <Input
                value={form.eyebrow}
                onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
              />
            </Field>
            <Field label="Badge">
              <Input
                value={form.badge}
                onChange={(e) => setForm({ ...form, badge: e.target.value })}
                placeholder="Fictional case study"
              />
            </Field>
          </div>
          <Field label="Headline" required>
            <Input
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder="Great revenue. One big customer."
              required
            />
          </Field>
          <Field label="Rail teaser">
            <Textarea
              rows={2}
              value={form.teaser}
              onChange={(e) => setForm({ ...form, teaser: e.target.value })}
            />
          </Field>
          <Field label="Scenario">
            <Textarea
              rows={4}
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              placeholder="Shown in full once the reader opens the case."
            />
          </Field>

          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Metrics
            </legend>
            <div className="space-y-2">
              {form.metrics.map((m, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-2">
                  <Input
                    value={m.label}
                    onChange={(e) =>
                      setForm({ ...form, metrics: setRow(form.metrics, i, { ...m, label: e.target.value }) })
                    }
                    placeholder="Revenue"
                  />
                  <Input
                    value={m.value}
                    onChange={(e) =>
                      setForm({ ...form, metrics: setRow(form.metrics, i, { ...m, value: e.target.value }) })
                    }
                    placeholder="$1.2M"
                  />
                </div>
              ))}
            </div>
            {form.metrics.length < 4 && (
              <button
                type="button"
                onClick={() => setForm({ ...form, metrics: [...form.metrics, { label: "", value: "" }] })}
                className="mt-2 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
              >
                + Add a metric
              </button>
            )}
          </fieldset>

          <fieldset className="rounded-xl border border-border p-4">
            <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Options and feedback
            </legend>
            <div className="space-y-3">
              {form.options.map((o, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-border/70 p-3">
                  <Input
                    value={o.label}
                    onChange={(e) =>
                      setForm({ ...form, options: setRow(form.options, i, { ...o, label: e.target.value }) })
                    }
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                  />
                  <Textarea
                    rows={2}
                    value={o.feedback}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        options: setRow(form.options, i, { ...o, feedback: e.target.value }),
                      })
                    }
                    placeholder="What the reader is told after choosing this."
                  />
                  {form.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) })
                      }
                      className="text-xs font-semibold text-rose-600 transition-opacity hover:opacity-80"
                    >
                      Remove this option
                    </button>
                  )}
                </div>
              ))}
            </div>
            {form.options.length < 6 && (
              <button
                type="button"
                onClick={() => setForm({ ...form, options: [...form.options, { label: "", feedback: "" }] })}
                className="mt-2 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
              >
                + Add an option
              </button>
            )}
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Related lesson">
              <select
                value={form.lesson_id}
                onChange={(e) => setForm({ ...form, lesson_id: e.target.value })}
                className={SELECT_CLASS}
              >
                <option value="">None</option>
                {lessons.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Display order">
              <Input
                type="number"
                min={0}
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
              />
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as AcademyStatus })}
                className={SELECT_CLASS}
              >
                {ACADEMY_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {ACADEMY_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </SmallFormModal>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1.5 block">
        {label}
        {required && <span className="ml-0.5 text-rose-500">*</span>}
      </Label>
      {children}
    </div>
  );
}

function IconButton({
  title,
  onClick,
  danger,
  as,
  href,
  children,
}: {
  title: string;
  onClick?: () => void;
  danger?: boolean;
  as?: "a";
  href?: string;
  children: React.ReactNode;
}) {
  const className = cn(
    "flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors",
    danger ? "hover:border-rose-500/40 hover:text-rose-600" : "hover:border-primary/50 hover:text-primary",
  );
  if (as === "a") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        aria-label={title}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} className={className}>
      {children}
    </button>
  );
}

function SmallFormModal({
  title,
  saving,
  wide,
  onCancel,
  onSubmit,
  children,
}: {
  title: string;
  saving: boolean;
  wide?: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={cn(
          "my-6 w-full overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",
          wide ? "max-w-3xl" : "max-w-xl",
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <strong className="text-sm font-bold text-foreground">{title}</strong>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="space-y-4 p-5"
        >
          {children}
          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
